import { changeSelectedSla } from '@vtex/delivery-packages/dist/sla'
import { last, uniq, flatten, isEmpty } from 'ramda'

import { useShippingActionsLegacy } from '../../../../../checkout-instore-bridge/hooks/shippingActionsLegacy'
import { useShipping } from '../../../../../core/globalState/shipping'
import { useCustomConfig } from '../../../../../core/hooks/useCustomConfig'
import type { OrderFormItem } from '../../../../../core/requests/checkout'
import { useOrderForm } from '../../../../../core/requests/checkout'
import {
  checkAccountHasTakeAwayAvailable,
  getSelectedSlaFromLogisticInfoItem,
} from '../../../../../core/utils/orderForm'
import { usePackages } from '../../../hooks'
import {
  selectCheapestSlaForNonTakeAwayItems,
  selectDefaultSla,
  selectFastestSlaForNonTakeAwayItems,
  selectPickup,
} from '../../../utils/shipping'
import { DeliveryChannel, ShippingMethod } from '../../../constants'

export const useShippingMethodSelection = () => {
  const { orderForm, updateCartItem } = useOrderForm()
  const useShippingLegacy = useShippingActionsLegacy()
  const { sellWithoutStockInHands } = useCustomConfig()
  const { shipping } = useShipping()
  const packages = usePackages()

  const deliveryChannels =
    orderForm?.shippingData?.logisticsInfo?.map((itemLogisticsInfo) =>
      itemLogisticsInfo.slas.map((sla) => sla.deliveryChannel)
    ) ?? []

  const hasPickupDeliveryChannel = flatten(deliveryChannels).some(
    (deliveryChannel) => deliveryChannel === DeliveryChannel.PickupInPoint
  )

  const hasOnlyPickupDeliveryChannel = uniq(flatten(deliveryChannels)).every(
    (deliveryChannel) => deliveryChannel === DeliveryChannel.PickupInPoint
  )

  const hasTakeaway = () =>
    checkAccountHasTakeAwayAvailable(orderForm) || sellWithoutStockInHands

  const getAvailableShippingMethods = () => {
    if (isEmpty(deliveryChannels)) {
      return []
    }

    if (hasOnlyPickupDeliveryChannel) {
      const pickupShippingMethods = [ShippingMethod.PickUp]

      if (hasTakeaway()) {
        pickupShippingMethods.push(ShippingMethod.TakeAway)
      }

      return pickupShippingMethods
    }

    const availableShippingMethods = [ShippingMethod.Customize]

    if (hasPickupDeliveryChannel) {
      availableShippingMethods.push(ShippingMethod.PickUp)
    }

    if (hasTakeaway()) {
      availableShippingMethods.push(ShippingMethod.TakeAway)
    }

    availableShippingMethods.push(ShippingMethod.Cheapest)
    availableShippingMethods.push(ShippingMethod.Fastest)

    return availableShippingMethods
  }

  const updateLogisticsInfoToTakeAway = async () => {
    const logisticsInfoToForceTakeAway =
      sellWithoutStockInHands && orderForm
        ? orderForm.shippingData.logisticsInfo.filter((logisticInfo) => {
            const hasTakeAwaySlaAvailable = logisticInfo.slas.some(
              (sla) => sla.pickupPointId === orderForm.checkedInPickupPointId
            )

            return !hasTakeAwaySlaAvailable
          })
        : []

    const promisesOrderForm =
      logisticsInfoToForceTakeAway.length > 0
        ? logisticsInfoToForceTakeAway.map((logisticInfo) => {
            const item = orderForm?.items[
              logisticInfo.itemIndex
            ] as OrderFormItem

            return updateCartItem(item.id, 0, item.quantity, item.seller, true)
          })
        : [Promise.resolve(orderForm)]

    const newOrderForm = await Promise.all(promisesOrderForm).then((results) =>
      last(results)
    )

    const newLogisticsInfo = newOrderForm?.shippingData.logisticsInfo.map(
      (logisticInfo) => {
        const alreadyForcedTakeAway = logisticsInfoToForceTakeAway.some(
          ({ itemId }) => itemId === logisticInfo.itemId
        )

        const selectedSla = getSelectedSlaFromLogisticInfoItem(logisticInfo)

        const isTakeAway =
          selectedSla?.pickupPointId === orderForm?.checkedInPickupPointId

        if (isTakeAway || alreadyForcedTakeAway) {
          return logisticInfo
        }

        const slaTakeAway = logisticInfo.slas.find(
          (sla) => sla.pickupPointId === orderForm?.checkedInPickupPointId
        )

        return slaTakeAway
          ? changeSelectedSla(logisticInfo, slaTakeAway)
          : logisticInfo
      }
    )

    useShippingLegacy.sendLogisticsInfoOnShippingData(
      newLogisticsInfo ?? [],
      orderForm
    )
  }

  const updateLogisticsInfoToFatest = () => {
    const newLogisticsInfo = selectFastestSlaForNonTakeAwayItems(orderForm)

    useShippingLegacy.sendLogisticsInfoOnShippingData(
      newLogisticsInfo,
      orderForm
    )
  }

  const updateLogisticsInfoToCheapest = () => {
    const newLogisticsInfo = selectCheapestSlaForNonTakeAwayItems(orderForm)

    useShippingLegacy.sendLogisticsInfoOnShippingData(
      newLogisticsInfo,
      orderForm
    )
  }

  const updateLogisticsInfoToPickup = () => {
    const newLogisticsInfo =
      selectPickup(orderForm?.shippingData?.logisticsInfo, orderForm) ?? []

    useShippingLegacy.sendLogisticsInfoOnShippingData(
      newLogisticsInfo,
      orderForm
    )
  }

  const updateLogisticsInfoToDefaultSelectedSla = () => {
    const newLogisticsInfo =
      selectDefaultSla(orderForm?.shippingData?.logisticsInfo, orderForm) ?? []

    useShippingLegacy.sendLogisticsInfoOnShippingData(
      newLogisticsInfo,
      orderForm
    )
  }

  return {
    updateLogisticsInfoToTakeAway,
    updateLogisticsInfoToFatest,
    updateLogisticsInfoToCheapest,
    updateLogisticsInfoToPickup,
    updateLogisticsInfoToDefaultSelectedSla,
    loading: shipping.loading,
    packages,
    hasOnlyPickupDeliveryChannel,
    getAvailableShippingMethods,
  }
}
